/**
 * File discovery and naming for the deliverables download.
 *
 * The S3 result folder holds the engine's outputs alongside its inputs and intermediates, and the
 * names it writes are not the names customers should receive. These are pure functions over a
 * listing so the rules stay testable and out of the download handler.
 */

/** Suffix the engine appends to every one-page report (`gentian-ml` main.py). */
const REPORT_SUFFIX = '_TrueSight_Gentian.pdf';

/**
 * The supported GIS deliverables, mapping each engine output to the stem customers receive.
 *
 * This is an allowlist, and deliberately so. The result folder also holds the engine's inputs and
 * working files — AOI.geojson, aoi_tiles.geojson, OS_accounting.geojson, the customer's own AOI —
 * all of which are valid GeoJSON. Excluding them by name meant a new intermediate leaked into the
 * download every time the pipeline changed; only shipping what we recognise cannot.
 *
 * A survey produces whichever of these apply, so absences are normal: a site with no linear
 * habitats has no LINEAR_habitats.geojson.
 */
const GEOJSON_STEMS: Record<string, string> = {
  'hesti-habitats-bng.geojson': 'habitats',
  // Both spellings are live: the engine's default is the hyphen, but the filename is config-driven
  // and current deployments write the underscore. Existing projects hold either.
  'linear-habitats.geojson': 'linear',
  'linear_habitats.geojson': 'linear',
  'tree-points.geojson': 'treepoints',
};

/** Last path segment of an S3 key. */
export const fileNameOf = (key: string): string => key.split('/').pop() || key;

/** The report PDF, or undefined when the folder has none. */
export const findReportKey = (keys: string[]): string | undefined =>
  keys.find((key) => fileNameOf(key).toLowerCase().endsWith('.pdf'));

/** The supported GIS deliverables present in a folder. Everything else is ignored. */
export const selectGeoJsonKeys = (keys: string[]): string[] =>
  keys.filter((key) => fileNameOf(key).toLowerCase() in GEOJSON_STEMS);

/**
 * GeoJSONs in the folder that we do not recognise.
 *
 * They are never downloaded, but a genuinely new output type would otherwise be dropped in
 * silence — logging these is how it gets noticed and added to the allowlist.
 */
export const unmappedGeoJsonNames = (keys: string[]): string[] =>
  keys
    .map(fileNameOf)
    .filter((fileName) => fileName.toLowerCase().endsWith('.geojson'))
    .filter((fileName) => !(fileName.toLowerCase() in GEOJSON_STEMS));

/** The engine's own sanitiser (gentian-ml main.py): spaces to dashes, commas and slashes dropped. */
const sanitiseProjectName = (name: string): string => name.replace(/\s+/g, '-').replace(/[,/]/g, '');

/** The `<date>` from a key sitting in a `<date>_<projectId>/` folder. */
const folderDateFrom = (keys: string[]): string | undefined =>
  keys[0]?.split('/').slice(-2, -1)[0]?.split('_')[0];

/**
 * The shared prefix for every downloaded file: the project's *current* name and the delivery date.
 *
 * Taken from the project record rather than from the report's filename. The engine names the PDF
 * after whatever the project was called when the survey ran, so reading the name back off that
 * filename meant a renamed project kept downloading files under its old name. It also means a
 * report whose filename does not follow the convention no longer drags the whole naming with it.
 *
 * The S3 objects keep their original names — only the copies in the download are renamed.
 */
export const resolveBaseName = (keys: string[], projectName?: string): string => {
  const safeName = sanitiseProjectName(projectName || 'deliverables');
  const date = folderDateFrom(keys);

  return date ? `${safeName}_${date}` : safeName;
};

/**
 * The project name the report was generated under, recovered from its own filename.
 *
 * `Test-latest-dev_2026-07-16_TrueSight_Gentian.pdf` -> `Test-latest-dev`
 *
 * Used only to locate the title inside the document — the engine writes the name with spaces into
 * the PDF but dashes into the filename, so this is matched loosely rather than compared exactly.
 * Undefined when the filename does not follow the convention, in which case there is nothing to
 * match against and the first text operator is used instead.
 */
export const reportTitleHint = (reportFileName: string): string | undefined => {
  if (!reportFileName.toLowerCase().endsWith(REPORT_SUFFIX.toLowerCase())) return undefined;

  const withoutSuffix = reportFileName.slice(0, -REPORT_SUFFIX.length);
  const name = withoutSuffix.replace(/_\d{4}-\d{2}-\d{2}$/, '');

  return name || undefined;
};

/** The report's name in the download, following the same convention the engine uses. */
export const reportFileName = (baseName: string): string => `${baseName}${REPORT_SUFFIX}`;

/**
 * The stem a GeoJSON is delivered under — without extension, so Shapefile sidecars can share it.
 *
 * `hesti-habitats-BNG.geojson` + `LUC-Test_2026-08-06` -> `LUC-Test_2026-08-06_habitats`
 */
export const downloadStemFor = (fileName: string, baseName: string): string =>
  `${baseName}_${GEOJSON_STEMS[fileName.toLowerCase()]}`;

/** Name of the archive itself. */
export const deliverablesZipName = (baseName: string): string =>
  `${baseName}${REPORT_SUFFIX.replace(/\.pdf$/i, '.zip')}`;
